import React, { useState } from 'react';
import { X, MessageSquarePlus } from 'lucide-react';

const CreateFeedbackModal = ({ isOpen, onClose, onSuccess }) => {
    const [title, setTitle] = useState('');
    const [feedback, setFeedback] = useState('');
    const [errors, setErrors] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Client-side validation
        const validationErrors = [];
        if (!title.trim()) {
            validationErrors.push('Titel angeben');
        }
        if (!feedback.trim()) {
            validationErrors.push('Beschreibung angeben');
        }

        if (validationErrors.length > 0) {
            setErrors(validationErrors);
            return;
        }

        setIsSubmitting(true);
        setErrors([]);

        try {
            const response = await fetch('index.php/feedbacks/create', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    title: title.trim(),
                    feedback: feedback.trim(),
                }),
            });

            const result = await response.json();

            if (response.ok && result.success) {
                // Reset form
                setTitle('');
                setFeedback('');
                onSuccess?.(result.message);
                onClose();
            } else {
                setErrors(result.errors || [result.error || 'Fehler beim Erstellen']);
            }
        } catch (error) {
            console.error('Error creating feedback:', error);
            setErrors(['Netzwerkfehler beim Erstellen des Feedbacks']);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setTitle('');
        setFeedback('');
        setErrors([]);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1050,
            }}
            onClick={handleClose}
        >
            <div
                style={{
                    backgroundColor: 'var(--bg-card, #ffffff)',
                    borderRadius: '12px',
                    width: '100%',
                    maxWidth: '600px',
                    maxHeight: '90vh',
                    overflow: 'hidden',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '1.25rem 1.5rem',
                        borderBottom: '1px solid var(--border-light, #e2e8f0)',
                    }}
                >
                    <h3
                        style={{
                            margin: 0,
                            fontSize: '1.125rem',
                            fontWeight: 600,
                            color: 'var(--text-primary, #2c3e50)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                        }}
                    >
                        <MessageSquarePlus size={22} />
                        Neues Feedback
                    </h3>
                    <button
                        onClick={handleClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px',
                            color: 'var(--text-secondary, #64748b)',
                        }}
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <form onSubmit={handleSubmit}>
                    <div style={{ padding: '1.5rem' }}>
                        {/* Error Messages */}
                        {errors.length > 0 && (
                            <div
                                style={{
                                    padding: '0.75rem 1rem',
                                    marginBottom: '1rem',
                                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                    border: '1px solid rgba(239, 68, 68, 0.3)',
                                    borderRadius: '8px',
                                    color: '#dc2626',
                                }}
                            >
                                {errors.map((error, index) => (
                                    <div key={index}>{error}</div>
                                ))}
                            </div>
                        )}

                        {/* Title Input */}
                        <div style={{ marginBottom: '1rem' }}>
                            <label
                                style={{
                                    display: 'block',
                                    marginBottom: '0.5rem',
                                    fontWeight: 500,
                                    color: 'var(--text-primary, #2c3e50)',
                                    fontSize: '0.875rem',
                                }}
                            >
                                Titel *
                            </label>
                            <input
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="Kurze Beschreibung des Feedbacks"
                                style={{
                                    width: '100%',
                                    padding: '0.75rem 1rem',
                                    border: '1px solid var(--border-medium, #cbd5e1)',
                                    borderRadius: '8px',
                                    fontSize: '0.9375rem',
                                    backgroundColor: 'var(--bg-card, #ffffff)',
                                    color: 'var(--text-primary, #2c3e50)',
                                    outline: 'none',
                                    transition: 'border-color 0.2s ease',
                                }}
                                onFocus={(e) => (e.target.style.borderColor = 'var(--accent-slate, #5b7c8d)')}
                                onBlur={(e) => (e.target.style.borderColor = 'var(--border-medium, #cbd5e1)')}
                            />
                        </div>

                        {/* Feedback Textarea */}
                        <div style={{ marginBottom: '1rem' }}>
                            <label
                                style={{
                                    display: 'block',
                                    marginBottom: '0.5rem',
                                    fontWeight: 500,
                                    color: 'var(--text-primary, #2c3e50)',
                                    fontSize: '0.875rem',
                                }}
                            >
                                Beschreibung *
                            </label>
                            <textarea
                                value={feedback}
                                onChange={(e) => setFeedback(e.target.value)}
                                placeholder="Detaillierte Beschreibung Ihres Feedbacks, Vorschlags oder Problems..."
                                rows={8}
                                style={{
                                    width: '100%',
                                    padding: '0.75rem 1rem',
                                    border: '1px solid var(--border-medium, #cbd5e1)',
                                    borderRadius: '8px',
                                    fontSize: '0.9375rem',
                                    backgroundColor: 'var(--bg-card, #ffffff)',
                                    color: 'var(--text-primary, #2c3e50)',
                                    outline: 'none',
                                    resize: 'vertical',
                                    minHeight: '150px',
                                    transition: 'border-color 0.2s ease',
                                }}
                                onFocus={(e) => (e.target.style.borderColor = 'var(--accent-slate, #5b7c8d)')}
                                onBlur={(e) => (e.target.style.borderColor = 'var(--border-medium, #cbd5e1)')}
                            />
                        </div>
                    </div>

                    {/* Footer */}
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '0.75rem',
                            padding: '1rem 1.5rem',
                            borderTop: '1px solid var(--border-light, #e2e8f0)',
                            backgroundColor: 'var(--bg-secondary, #f5f7f9)',
                        }}
                    >
                        <button
                            type="button"
                            onClick={handleClose}
                            style={{
                                padding: '0.5rem 1rem',
                                border: '1px solid var(--border-medium, #cbd5e1)',
                                borderRadius: '8px',
                                backgroundColor: 'var(--bg-card, #ffffff)',
                                color: 'var(--text-primary, #2c3e50)',
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                cursor: 'pointer',
                            }}
                        >
                            Abbrechen
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            style={{
                                padding: '0.5rem 1rem',
                                border: 'none',
                                borderRadius: '8px',
                                backgroundColor: 'var(--accent-slate, #5b7c8d)',
                                color: '#ffffff',
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                opacity: isSubmitting ? 0.7 : 1,
                            }}
                        >
                            {isSubmitting ? 'Wird erstellt...' : 'Feedback einreichen'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateFeedbackModal;
